<script setup lang="ts">
import {ref, watch} from "vue";
import FlowchartNotificationSequence from "@/flowcharts/Components/flowchart-modal/FlowchartNotificationSequence.vue";
import {NotificationSequence} from "@/notification-sequences/interfaces/NotificationSequence";
import {NotificationSequenceCard} from "@/notification-sequences/components/notification-sequence-card";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";

/**
 * Wraps an action page and swaps it for a notification sequence detail page
 * when the user opens one of the node's linked sequences.
 */
const props = defineProps<{
    sequences: NotificationSequence[];
    /** The patient the flowchart is run for. */
    consumer: Customer | Beneficiary;
}>();

const emits = defineEmits<{
    (e: 'update:viewing-sequence', value: boolean): void;
}>();

/** The sequence currently open in the detail page, if any. */
const selectedSequence = ref<NotificationSequence | null>(null);

watch(selectedSequence, (value) => {
    emits('update:viewing-sequence', !!value);
});

/** The slide transition to use when switching page. */
const pageTransition = ref<'slide-left' | 'slide-right'>('slide-left');

/**
 * Open the detail page for a sequence.
 */
const openSequence = (sequence: NotificationSequence) => {
    pageTransition.value = 'slide-left';
    selectedSequence.value = sequence;
};

/**
 * Go back to the wrapped action page.
 */
const closeSequence = () => {
    pageTransition.value = 'slide-right';
    selectedSequence.value = null;
};
</script>

<template>
  <Transition :name="pageTransition" mode="out-in">
    <div v-if="!selectedSequence">
      <slot />
      <NotificationSequenceCard
        v-for="sequence in props.sequences"
        :key="sequence.id"
        class="mt-5 bg-secondary/25 border-2 border-secondary"
        :sequence="sequence"
        :toggleable="false"
        cta-label="Découvrir la séquence"
        :new-tag="true"
        @navigate="openSequence"
      />
    </div>
    <FlowchartNotificationSequence
      v-else
      :sequence="selectedSequence"
      :consumer="props.consumer"
      @back="closeSequence"
      @activated="closeSequence"
    />
  </Transition>
</template>

<style scoped>
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.25s ease, opacity 0.25s ease;
}

.slide-left-enter-from,
.slide-right-leave-to {
    transform: translateX(100%);
    opacity: 0;
}

.slide-left-leave-to,
.slide-right-enter-from {
    transform: translateX(-100%);
    opacity: 0;
}
</style>
